import { Helmet } from "react-helmet";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Problem from "@/components/Problem";
import Solution from "@/components/Solution";
import VisualProof from "@/components/VisualProof";
import Trust from "@/components/Trust";
import Testimonials from "@/components/Testimonials";
import HowItWorks from "@/components/HowItWorks";
import MaturityCheckCTA from "@/components/MaturityCheckCTA";
import FinalCTA from "@/components/FinalCTA";
import Footer from "@/components/Footer";
import StickyCTABar from "@/components/shared/StickyCTABar";
import ExitIntentPopup from "@/components/shared/ExitIntentPopup";
import { useScrollDepthTracking } from "@/hooks/useScrollDepthTracking";
import logoAhmrc from "@/assets/logos/ahmrc.png.asset.json";
import logoIntercept from "@/assets/logos/intercept-group.png.asset.json";
import logoTts from "@/assets/logos/tts.jpg.asset.json";
import logoTtsa from "@/assets/logos/ttsa.png.asset.json";
import logoThink from "@/assets/logos/think-real-estate.jpg.asset.json";
import logoRets from "@/assets/logos/rets.jpg.asset.json";
import logoReinsw from "@/assets/logos/reinsw.png.asset.json";
import logoNewcastle from "@/assets/logos/newcastle-rescue.jpg.asset.json";
import logoMelloz from "@/assets/logos/melloz.png.asset.json";
import logoIndie from "@/assets/logos/indie-education.png.asset.json";
import logoHpa from "@/assets/logos/hpa-training.png.asset.json";
import logoEvolve from "@/assets/logos/evolve-learning.png.asset.json";
import logoDijan from "@/assets/logos/dijan-training.png.asset.json";
import logoAst from "@/assets/logos/all-states-training.png.asset.json";

const rtoLogos = [
  { src: logoAhmrc.url, alt: "Aboriginal Health & Medical Research Council of NSW" },
  { src: logoIntercept.url, alt: "Intercept Group" },
  { src: logoTts.url, alt: "TTS Training" },
  { src: logoTtsa.url, alt: "TTSA Training Australia" },
  { src: logoThink.url, alt: "Think Real Estate" },
  { src: logoRets.url, alt: "Real Estate Training Solutions (RETS)" },
  { src: logoReinsw.url, alt: "Real Estate Institute of NSW (REINSW)" },
  { src: logoNewcastle.url, alt: "Newcastle Rescue and Consultancy" },
  { src: logoMelloz.url, alt: "Melloz Services" },
  { src: logoIndie.url, alt: "Indie Education" },
  { src: logoHpa.url, alt: "HPA Training" },
  { src: logoEvolve.url, alt: "Evolve Learning Institute" },
  { src: logoDijan.url, alt: "Dijan Training" },
  { src: logoAst.url, alt: "All States Training (AST)" },
];

const Index = () => {
  // Track scroll depth on homepage
  useScrollDepthTracking('Homepage');

  return (
    <main className="min-h-screen">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "ComplyHub",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "description": "SaaS compliance platform for Australian Registered Training Organisations, providing audit-ready evidence mapping, document management, and ASQA audit preparation under the Standards for RTOs 2025.",
          "url": "https://complyhub.ai/",
          "publisher": { "@id": "https://complyhub.ai/#organization" },
          "offers": [
            { "@type": "Offer", "name": "Starter", "price": "295.00", "priceCurrency": "AUD", "url": "https://complyhub.ai/pricing",
              "priceSpecification": { "@type": "UnitPriceSpecification", "price": "295.00", "priceCurrency": "AUD", "valueAddedTaxIncluded": false, "unitText": "MONTH" } },
            { "@type": "Offer", "name": "Growth", "price": "450.00", "priceCurrency": "AUD", "url": "https://complyhub.ai/pricing",
              "priceSpecification": { "@type": "UnitPriceSpecification", "price": "450.00", "priceCurrency": "AUD", "valueAddedTaxIncluded": false, "unitText": "MONTH" } },
            { "@type": "Offer", "name": "Scale", "price": "625.00", "priceCurrency": "AUD", "url": "https://complyhub.ai/pricing",
              "priceSpecification": { "@type": "UnitPriceSpecification", "price": "625.00", "priceCurrency": "AUD", "valueAddedTaxIncluded": false, "unitText": "MONTH" } },
            { "@type": "Offer", "name": "Enterprise", "price": "950.00", "priceCurrency": "AUD", "url": "https://complyhub.ai/pricing",
              "priceSpecification": { "@type": "UnitPriceSpecification", "price": "950.00", "priceCurrency": "AUD", "valueAddedTaxIncluded": false, "unitText": "MONTH" } }
          ]
        })}</script>
      </Helmet>
      <StickyCTABar />
      <ExitIntentPopup />
      <Header />
      <Hero />

      {/* Stat bar — inline, homepage only */}
      <section
        style={{ backgroundColor: "rgba(223, 216, 232, 0.4)" }}
        className="py-10 px-6 md:py-14 md:px-20"
      >
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3">
          <div className="text-center px-6 py-6 md:py-0">
            <div className="font-heading text-ch-purple text-5xl">57</div>
            <div className="mt-2 font-carlito text-ch-acai text-sm uppercase tracking-widest">
              platform features
            </div>
          </div>
          <div
            className="text-center px-6 py-6 md:py-0 border-t md:border-t-0 md:border-l"
            style={{
              borderTopColor: "rgba(113,48,160,0.15)",
              borderLeftColor: "rgba(113,48,160,0.2)",
            }}
          >
            <div className="font-heading text-ch-purple text-5xl">29</div>
            <div className="mt-2 font-carlito text-ch-acai text-sm uppercase tracking-widest">
              integrated compliance registers
            </div>
          </div>
          <div
            className="text-center px-6 py-6 md:py-0 border-t md:border-t-0 md:border-l"
            style={{
              borderTopColor: "rgba(113,48,160,0.15)",
              borderLeftColor: "rgba(113,48,160,0.2)",
            }}
          >
            <div className="font-heading text-ch-purple text-5xl">5</div>
            <div className="mt-2 font-carlito text-ch-acai text-sm uppercase tracking-widest">
              dedicated role portals
            </div>
          </div>
        </div>
      </section>

      {/* Logo strip — placeholder slots; Angela to replace src values with real logos in src/assets/ and remove grey wrappers */}
      <section className="bg-white py-10 px-6 md:py-12 md:px-20">
        <div className="max-w-6xl mx-auto">
          <p className="text-center font-carlito text-ch-acai text-sm uppercase tracking-widest mb-6">
            Trusted by Australian RTOs
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8">
            {rtoLogos.map((logo) => (
              <div
                key={logo.alt}
                className="basis-[calc(50%-1rem)] md:basis-auto flex items-center justify-center"
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="h-12 w-auto object-contain"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <Problem />
      <Solution />
      <VisualProof />
      <Trust />
      <Testimonials />
      <HowItWorks />
      <FinalCTA />
      <MaturityCheckCTA />
      <Footer />
    </main>
  );
};

export default Index;
